import { useState } from "react";
import { Smartphone, Apple, Bot, ChevronDown, Share, Plus } from "lucide-react";

// Detect a likely platform so we can default-open the right tab,
// but never gate access — sometimes admins are looking on desktop.
const detectPlatform = () => {
  if (typeof window === "undefined") return "ios";
  const ua = window.navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  return "ios";
};

const IOS_STEPS = [
  { label: "Open this page in Safari", detail: "Chrome and other browsers on iPhone can't add to home screen." },
  { label: "Tap the Share button", icon: Share, detail: "It's the square with the arrow pointing up — at the bottom of the screen in Safari." },
  { label: "Scroll down and tap \"Add to Home Screen\"", icon: Plus, detail: "If you don't see it, scroll the share menu down — it's below options like Copy and Print." },
  { label: "Tap \"Add\" in the top-right", detail: "You can edit the name if you like — \"Savi\" is the default." },
  { label: "Open the Savi icon from your home screen", detail: "It opens full-screen, no browser bar, and notifications can be enabled." },
];

const ANDROID_STEPS = [
  { label: "Open this page in Chrome", detail: "Samsung Internet and Edge also work — Firefox is hit-or-miss." },
  { label: "Tap the menu button (⋮) in the top-right", detail: "Three vertical dots." },
  { label: "Tap \"Install app\" or \"Add to Home screen\"", icon: Plus, detail: "Newer Chrome shows \"Install app\". Older versions show \"Add to Home screen\" — either works." },
  { label: "Tap \"Install\" to confirm", detail: "The app icon will appear on your home screen and in your app drawer." },
  { label: "Open Savi from your home screen", detail: "Full-screen, native feel, and offline-ready." },
];

/** Big, scannable, copy-friendly install instructions. Used in the Profile tab so guests can always find it. */
export const InstallInstructions = ({ defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [tab, setTab] = useState(detectPlatform());

  const steps = tab === "ios" ? IOS_STEPS : ANDROID_STEPS;

  return (
    <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden" data-testid="install-instructions">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-stone-50/60 transition-colors"
        data-testid="install-instructions-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#E07A5F]/15 flex items-center justify-center">
            <Smartphone className="w-4 h-4 text-[#E07A5F]" />
          </div>
          <div>
            <p className="font-medium text-[#3D4035] text-sm">Save Savi to your phone</p>
            <p className="text-xs text-[#3D4035]/55">Opens full-screen, no browser bar, works offline.</p>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-[#3D4035]/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4">
          {/* Platform tabs */}
          <div className="flex gap-2" role="tablist">
            {[
              { id: "ios", label: "iPhone / iPad", Icon: Apple },
              { id: "android", label: "Android", Icon: Bot },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={`flex-1 flex items-center justify-center gap-2 text-sm py-2 rounded-xl border transition-colors ${
                  tab === id
                    ? "bg-[#3D4035] text-white border-[#3D4035]"
                    : "bg-white text-[#3D4035]/70 border-stone-200 hover:border-stone-300"
                }`}
                data-testid={`install-instructions-tab-${id}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {/* Steps */}
          <ol className="space-y-3" data-testid={`install-instructions-steps-${tab}`}>
            {steps.map((s, i) => (
              <li key={i} className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-[#F4F1DE] text-[#3D4035] text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#3D4035] leading-snug flex items-center gap-1.5 flex-wrap">
                    {s.icon && <s.icon className="w-3.5 h-3.5 text-[#E07A5F] inline" />}
                    {s.label}
                  </p>
                  {s.detail && <p className="text-xs text-[#3D4035]/55 mt-0.5 leading-relaxed">{s.detail}</p>}
                </div>
              </li>
            ))}
          </ol>

          <p className="text-[11px] text-[#3D4035]/45 leading-relaxed pt-2 border-t border-stone-100">
            Already installed? You should see a Savi icon on your home screen — tap it instead of opening Safari/Chrome.
          </p>
        </div>
      )}
    </div>
  );
};

export default InstallInstructions;
